import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Home,
  Loader2,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  Download,
  Printer,
  ArrowLeft,
  Mail,
  Clock,
  MapPin,
  DollarSign,
  Calendar,
  XCircle,
  RefreshCw,
  Lock,
  Unlock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ContractWiserLogo, ContractWiserLogoIcon } from "@/components/ContractWiserLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState("checking");
  const [submissionData, setSubmissionData] = useState(null);
  const [error, setError] = useState(null);
  const [resolvedSubmissionId, setResolvedSubmissionId] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [lockedCounts, setLockedCounts] = useState(null);
  const [isUnlocking, setIsUnlocking] = useState(false);
  
  // Timer state - 3 minutes = 180 seconds
  const [timeRemaining, setTimeRemaining] = useState(180);
  const [timerExpired, setTimerExpired] = useState(false);

  const submissionId = searchParams.get('submission_id');
  const sessionId = searchParams.get('session_id');
  const emailParam = searchParams.get('email');

  // Countdown timer
  useEffect(() => {
    if (loading && !timerExpired && status === "analyzing") {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setTimerExpired(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [loading, timerExpired, status]);

  useEffect(() => {
    document.title = "Your Report | ContractWiser";
    
    // Set email from URL param
    if (emailParam) {
      setCustomerEmail(emailParam);
    }
    
    // If we have a Stripe session_id, user is returning from payment
    if (sessionId && sessionId !== '{CHECKOUT_SESSION_ID}') {
      resolveSessionToSubmission();
      return;
    }
    
    // If we have submission_id directly, fetch report
    if (submissionId && submissionId !== '{CHECKOUT_SESSION_CLIENT_REFERENCE_ID}') {
      setResolvedSubmissionId(submissionId);
      fetchReport(submissionId);
      return;
    }
    
    setError("Missing submission information. Please upload your contract again.");
    setLoading(false);
  }, [submissionId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveSessionToSubmission = async () => {
    try {
      setStatus("checking");
      const response = await axios.get(`${API}/session/${sessionId}/submission`);
      const data = response.data;
      
      if (data.submission_id) {
        setResolvedSubmissionId(data.submission_id);
        window.history.replaceState(null, '', `/report?submission_id=${data.submission_id}`);
        
        // If payment was successful, confirm payment first
        if (data.payment_status === "paid") {
          await axios.post(`${API}/payment/confirm/${data.submission_id}`);
        }
        
        fetchReport(data.submission_id);
      } else {
        setError("Could not find your submission. Please contact support.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Session lookup error:", err);
      setError("Failed to verify payment. Please contact support at admin@contractwiser.com.au");
      setLoading(false);
    }
  };

  const fetchReport = async (subId, attempts = 0) => {
    const currentSubmissionId = subId || resolvedSubmissionId;
    const maxAttempts = 90;
    const pollInterval = 2000;

    if (!currentSubmissionId) {
      setError("Missing submission information. Please upload your contract again.");
      setLoading(false);
      return;
    }

    if (timerExpired) {
      setStatus("emailing");
      setLoading(false);
      return;
    }

    try {
      const email = customerEmail || emailParam;
      const reportResponse = await axios.get(`${API}/report/${currentSubmissionId}`, {
        params: email ? { email } : {}
      });
      const reportData = reportResponse.data;
      
      if (reportData.email) {
        setCustomerEmail(reportData.email);
      }
      
      setIsPaid(reportData.is_paid || false);
      setLockedCounts(reportData.locked_counts || null);
      
      if ((reportData.status === "completed" || reportData.status === "paid") && reportData.report) {
        setReport(reportData.report);
        setSubmissionData(reportData);
        setStatus("completed");
        setLoading(false);
        return;
      }
      
      if (reportData.status === "analyzing") {
        setStatus("analyzing");
        if (!timerExpired) {
          setTimeout(() => fetchReport(currentSubmissionId, attempts + 1), pollInterval);
        }
        return;
      }
      
      if (reportData.status === "failed") {
        setError("We encountered an issue analyzing your document. Our team has been notified and will contact you shortly at " + (reportData.email || "your email") + ".");
        setLoading(false);
        return;
      }
      
      // Still pending — poll again
      if (reportData.status === "pending") {
        setStatus("analyzing");
        if (!timerExpired && attempts < maxAttempts) {
          setTimeout(() => fetchReport(currentSubmissionId, attempts + 1), pollInterval);
        }
        return;
      }
      
    } catch (err) {
      console.error("Error:", err);
      if (attempts < 5 && !timerExpired) {
        setTimeout(() => fetchReport(currentSubmissionId, attempts + 1), pollInterval);
      } else if (!timerExpired) {
        setError("Failed to load your report. Please contact support at admin@contractwiser.com.au");
        setLoading(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUnlockReport = async () => {
    setIsUnlocking(true);
    const sid = resolvedSubmissionId || submissionId;
    try {
      const checkoutResponse = await axios.post(`${API}/checkout/create`, {
        submission_id: sid,
        origin_url: window.location.origin
      });
      window.location.href = checkoutResponse.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(err.response?.data?.detail || "Failed to create checkout. Please try again.");
      setIsUnlocking(false);
    }
  };

  const retryAnalysis = () => {
    setLoading(true);
    setError(null);
    setTimerExpired(false);
    setTimeRemaining(180);
    if (resolvedSubmissionId) {
      fetchReport(resolvedSubmissionId, 0);
    } else if (submissionId) {
      fetchReport(submissionId, 0);
    } else {
      setError("Missing submission information. Please upload your contract again.");
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    const sid = resolvedSubmissionId || submissionId;
    if (!sid) { toast.error("Report ID not available."); return; }
    try {
      toast.info("Generating PDF report...");
      const params = customerEmail ? { email: customerEmail } : {};
      const response = await axios.get(`${API}/report/${sid}/pdf`, { responseType: 'blob', params });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ContractWiser-Report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF report downloaded!");
    } catch (err) {
      toast.error("Failed to download report. Please try again.");
    }
  };

  const printReport = () => {
    const sid = resolvedSubmissionId || submissionId;
    if (!sid) { toast.error("Report ID not available."); return; }
    const emailP = customerEmail ? `?email=${encodeURIComponent(customerEmail)}` : '';
    const htmlUrl = `${API}/report/${sid}/html${emailP}`;
    const printWindow = window.open(htmlUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => setTimeout(() => printWindow.print(), 500);
    } else {
      toast.error("Unable to open print window.");
    }
  };

  const getConcernLevel = (level) => {
    switch (level) {
      case 'high': return { color: 'text-red-600 bg-red-50', icon: AlertTriangle };
      case 'medium': return { color: 'text-amber-600 bg-amber-50', icon: AlertTriangle };
      case 'low': return { color: 'text-blue-600 bg-blue-50', icon: HelpCircle };
      default: return { color: 'text-eucalypt-600 bg-eucalypt-50', icon: CheckCircle };
    }
  };

  const getProgressInfo = () => {
    const elapsed = 180 - timeRemaining;
    const progressFromTime = Math.min((elapsed / 180) * 60, 60);
    if (status === "pending" || status === "checking") return { percent: 10, label: "Starting analysis..." };
    if (status === "analyzing") return { percent: 30 + progressFromTime, label: "AI analyzing your contract..." };
    return { percent: 5, label: "Connecting..." };
  };

  const progressInfo = getProgressInfo();

  const hasLockedContent = lockedCounts && !isPaid && (
    lockedCounts.what_matters_most > 0 || 
    lockedCounts.special_conditions > 0 || 
    lockedCounts.things_to_clarify > 0 || 
    lockedCounts.questions_for_solicitor > 0
  );

  // Locked Section Overlay
  const LockedOverlay = ({ count, sectionName }) => (
    <div className="relative mt-4">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white z-10" />
      <div className="blur-sm opacity-50 pointer-events-none">
        {[...Array(Math.min(count, 3))].map((_, i) => (
          <div key={i} className="p-4 mb-3 bg-sandstone-100 border border-sandstone-200">
            <div className="h-4 bg-sandstone-300 rounded w-3/4 mb-2" />
            <div className="h-3 bg-sandstone-200 rounded w-full mb-1" />
            <div className="h-3 bg-sandstone-200 rounded w-2/3" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center z-20">
        <div className="text-center bg-white/95 backdrop-blur-sm px-6 py-4 rounded-lg shadow-lg border border-sandstone-200">
          <Lock className="h-6 w-6 text-eucalypt-600 mx-auto mb-2" />
          <p className="font-semibold text-eucalypt-800 text-sm">
            {count} more {sectionName} locked
          </p>
          <p className="text-xs text-muted-foreground mt-1">Unlock full report to view</p>
        </div>
      </div>
    </div>
  );

  // Fully Locked Section
  const FullyLockedSection = ({ count, sectionName, icon: Icon, bgClass }) => (
    <div className="relative">
      <div className="blur-sm opacity-40 pointer-events-none">
        {[...Array(Math.min(count, 4))].map((_, i) => (
          <div key={i} className={`p-4 mb-3 ${bgClass || 'bg-sandstone-50'} border border-sandstone-200`}>
            <div className="h-4 bg-sandstone-300 rounded w-3/4 mb-2" />
            <div className="h-3 bg-sandstone-200 rounded w-full mb-1" />
            <div className="h-3 bg-sandstone-200 rounded w-1/2" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="text-center bg-white/95 backdrop-blur-sm px-8 py-5 rounded-lg shadow-lg border border-sandstone-200">
          <Lock className="h-7 w-7 text-eucalypt-600 mx-auto mb-2" />
          <p className="font-semibold text-eucalypt-800">
            {count} {sectionName} locked
          </p>
          <p className="text-sm text-muted-foreground mt-1">Unlock the full report for $49.99 AUD</p>
        </div>
      </div>
    </div>
  );

  // "We'll email you" state
  if (timerExpired && status === "analyzing") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-lg w-full">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto bg-eucalypt-100 rounded-full flex items-center justify-center">
              <Mail className="h-10 w-10 text-eucalypt-700" />
            </div>
          </div>
          <h2 className="font-heading text-2xl text-eucalypt-800 mb-3">Your Document is Being Processed</h2>
          <p className="text-muted-foreground mb-6">Your contract is larger than usual and requires extra processing time.</p>
          <Card className="bg-eucalypt-50 border-eucalypt-200 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-eucalypt-600 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <p className="font-semibold text-eucalypt-800 mb-1">We'll email your report to:</p>
                  <p className="text-eucalypt-700 font-medium">{customerEmail || "your registered email"}</p>
                  <p className="text-sm text-eucalypt-600 mt-2">Usually within 30 minutes.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="mt-8 flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate("/")}><Home className="h-4 w-4 mr-2" />Home</Button>
            <Button onClick={() => { setTimerExpired(false); setTimeRemaining(60); setLoading(true); fetchReport(resolvedSubmissionId || submissionId); }} className="bg-eucalypt-700 hover:bg-eucalypt-800">
              <RefreshCw className="h-4 w-4 mr-2" />Check Again
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md w-full">
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto bg-eucalypt-100 rounded-full flex items-center justify-center">
              <FileText className="h-8 w-8 text-eucalypt-700" />
            </div>
          </div>
          <h2 className="font-heading text-2xl text-eucalypt-800 mb-2">Analyzing Your Contract</h2>
          <p className="text-muted-foreground mb-6">Our AI is reading through your contract and preparing your report.</p>
          {status === "analyzing" && (
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 bg-eucalypt-100 px-4 py-2 rounded-full">
                <Clock className="h-5 w-5 text-eucalypt-700" />
                <span className="font-mono text-xl font-bold text-eucalypt-800">{formatTime(timeRemaining)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Estimated time remaining</p>
            </div>
          )}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">{progressInfo.label}</span>
              <span className="text-eucalypt-700 font-semibold">{Math.round(progressInfo.percent)}%</span>
            </div>
            <div className="h-3 bg-sandstone-200 rounded-full overflow-hidden">
              <motion.div className="h-full bg-gradient-to-r from-eucalypt-600 to-terracotta-500 rounded-full" initial={{ width: 0 }} animate={{ width: `${progressInfo.percent}%` }} transition={{ duration: 0.5 }} />
            </div>
          </div>
          <div className="space-y-3 text-left bg-sandstone-50 p-4 rounded mb-6">
            <div className={`flex items-center gap-3 ${progressInfo.percent >= 20 ? 'text-eucalypt-700' : 'text-muted-foreground'}`}>
              {progressInfo.percent >= 30 ? <CheckCircle className="h-5 w-5 text-eucalypt-600" /> : progressInfo.percent >= 10 ? <Loader2 className="h-5 w-5 animate-spin" /> : <div className="h-5 w-5 rounded-full border-2 border-sandstone-300" />}
              <span className={progressInfo.percent >= 30 ? 'line-through opacity-60' : ''}>Uploading contract to AI</span>
            </div>
            <div className={`flex items-center gap-3 ${progressInfo.percent >= 40 ? 'text-eucalypt-700' : 'text-muted-foreground'}`}>
              {progressInfo.percent >= 80 ? <CheckCircle className="h-5 w-5 text-eucalypt-600" /> : progressInfo.percent >= 40 ? <Loader2 className="h-5 w-5 animate-spin" /> : <div className="h-5 w-5 rounded-full border-2 border-sandstone-300" />}
              <span>Analyzing clauses & conditions</span>
            </div>
            <div className={`flex items-center gap-3 ${progressInfo.percent >= 90 ? 'text-eucalypt-700' : 'text-muted-foreground'}`}>
              {progressInfo.percent >= 100 ? <CheckCircle className="h-5 w-5 text-eucalypt-600" /> : <div className="h-5 w-5 rounded-full border-2 border-sandstone-300" />}
              <span>Generating your report</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Please don't close this page. If processing takes longer, we'll email your report.</p>
        </motion.div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-100 flex items-center justify-center">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="font-heading text-2xl text-eucalypt-800 mb-2">Something Went Wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="retry-btn" onClick={retryAnalysis} className="bg-eucalypt-700 hover:bg-eucalypt-800 text-white"><RefreshCw className="h-4 w-4 mr-2" />Try Again</Button>
            <Button data-testid="home-btn" variant="outline" onClick={() => navigate('/')}><Home className="h-4 w-4 mr-2" />Back to Home</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Report View
  return (
    <div className="min-h-screen bg-sandstone-100">
      {/* Navigation */}
      <nav className="bg-white shadow-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <ContractWiserLogo size="default" />
          </button>
          <Button data-testid="new-analysis-btn" variant="outline" onClick={() => navigate('/upload')} className="text-eucalypt-700 border-eucalypt-300">
            Analyze Another Contract
          </Button>
        </div>
      </nav>

      <main className="py-8 px-4 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Banner */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className={`${isPaid ? 'bg-eucalypt-700' : 'bg-gradient-to-r from-eucalypt-700 to-eucalypt-800'} text-white p-4 mb-6 flex items-center justify-between`}
          >
            <div className="flex items-center gap-3">
              {isPaid ? <CheckCircle className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
              <span>{isPaid ? "Your contract analysis is complete!" : "Preview — unlock full report for complete analysis"}</span>
            </div>
            <span className="text-sandstone-200 text-sm">
              {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </motion.div>

          {/* Unlock CTA - Sticky top bar for unpaid */}
          {hasLockedContent && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="sticky top-0 z-40 bg-terracotta-600 text-white p-4 mb-6 flex items-center justify-between shadow-lg"
            >
              <div className="flex items-center gap-3">
                <Unlock className="h-5 w-5" />
                <div>
                  <p className="font-semibold text-sm">Unlock Full Report</p>
                  <p className="text-terracotta-100 text-xs">
                    {(lockedCounts?.what_matters_most || 0) + (lockedCounts?.special_conditions || 0) + (lockedCounts?.things_to_clarify || 0) + (lockedCounts?.questions_for_solicitor || 0)} more insights locked
                  </p>
                </div>
              </div>
              <Button
                data-testid="unlock-report-sticky-btn"
                onClick={handleUnlockReport}
                disabled={isUnlocking}
                className="bg-white text-terracotta-700 hover:bg-terracotta-50 font-semibold"
              >
                {isUnlocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlock className="h-4 w-4 mr-2" />}
                Unlock — $49.99 AUD
              </Button>
            </motion.div>
          )}

          {/* Report Document */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white shadow-float">
            {/* Report Header */}
            <div className="bg-eucalypt-800 px-8 py-8">
              <p className="text-sandstone-300 text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Contract Analysis Report {!isPaid && <span className="bg-terracotta-600 text-white text-xs px-2 py-0.5 rounded ml-2">PREVIEW</span>}
              </p>
              <h1 className="font-heading text-2xl md:text-3xl text-white mb-2">
                {report?.contract_snapshot?.address || "Property Contract Analysis"}
              </h1>
              {submissionData && (
                <p className="text-sandstone-300 text-sm">
                  {submissionData.property_type?.charAt(0).toUpperCase() + submissionData.property_type?.slice(1)} &bull; {submissionData.state}
                </p>
              )}
            </div>

            {/* Section 1: Contract Snapshot - Always fully visible */}
            <section className="p-8 border-b border-sandstone-200" data-testid="section-snapshot">
              <h2 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-eucalypt-100 flex items-center justify-center text-eucalypt-700 font-bold text-sm">1</span>
                Contract Snapshot
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {report?.contract_snapshot?.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-eucalypt-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Address</p>
                      <p className="text-eucalypt-800 font-medium">{report.contract_snapshot.address}</p>
                    </div>
                  </div>
                )}
                {report?.contract_snapshot?.purchase_price && (
                  <div className="flex items-start gap-3">
                    <DollarSign className="h-5 w-5 text-eucalypt-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Purchase Price</p>
                      <p className="text-2xl font-heading font-semibold text-eucalypt-800">{report.contract_snapshot.purchase_price}</p>
                    </div>
                  </div>
                )}
                {report?.contract_snapshot?.deposit && (
                  <div className="flex items-start gap-3">
                    <DollarSign className="h-5 w-5 text-eucalypt-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Deposit</p>
                      <p className="text-eucalypt-700">{report.contract_snapshot.deposit}</p>
                    </div>
                  </div>
                )}
                {report?.contract_snapshot?.settlement_date && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-eucalypt-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Settlement Date</p>
                      <p className="text-eucalypt-700">{report.contract_snapshot.settlement_date}</p>
                    </div>
                  </div>
                )}
                {report?.contract_snapshot?.cooling_off && (
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-eucalypt-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Cooling-Off Period</p>
                      <p className="text-eucalypt-700">{report.contract_snapshot.cooling_off}</p>
                    </div>
                  </div>
                )}
              </div>
              {report?.contract_snapshot?.other_key_dates?.length > 0 && (
                <div className="mt-6 p-4 bg-sandstone-50">
                  <p className="text-sm text-muted-foreground uppercase tracking-wide mb-3">Other Key Dates</p>
                  <ul className="space-y-2">
                    {report.contract_snapshot.other_key_dates.map((date, index) => (
                      <li key={index} className="flex items-center gap-2 text-eucalypt-700">
                        <Calendar className="h-4 w-4 text-eucalypt-500" />{date}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Section 2: What Matters Most */}
            <section className="p-8 border-b border-sandstone-200 bg-terracotta-50/30" data-testid="section-matters">
              <h2 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-terracotta-100 flex items-center justify-center text-terracotta-700 font-bold text-sm">2</span>
                What Matters Most
              </h2>
              {report?.what_matters_most?.length > 0 ? (
                <div className="space-y-4">
                  {report.what_matters_most.map((item, index) => (
                    <div key={index} className={`p-4 ${item.importance === 'high' ? 'bg-terracotta-100 border-l-4 border-terracotta-600' : 'bg-white border-l-4 border-eucalypt-400'}`}>
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${item.importance === 'high' ? 'text-terracotta-600' : 'text-eucalypt-600'}`} />
                        <div>
                          <p className="font-semibold text-eucalypt-800 mb-1">{item.clause}</p>
                          <p className="text-muted-foreground">{item.explanation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No critical clauses identified.</p>
              )}
              {!isPaid && lockedCounts?.what_matters_most > 0 && (
                <LockedOverlay count={lockedCounts.what_matters_most} sectionName="key clauses" />
              )}
            </section>

            {/* Section 3: Special Conditions */}
            <section className="p-8 border-b border-sandstone-200" data-testid="section-conditions">
              <h2 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-eucalypt-100 flex items-center justify-center text-eucalypt-700 font-bold text-sm">3</span>
                Special Conditions Explained
              </h2>
              {report?.special_conditions?.length > 0 ? (
                <Accordion type="single" collapsible className="space-y-3">
                  {report.special_conditions.map((condition, index) => {
                    const concernStyle = getConcernLevel(condition.concern_level);
                    return (
                      <AccordionItem key={index} value={`condition-${index}`} className={`border px-4 ${concernStyle.color}`}>
                        <AccordionTrigger data-testid={`condition-trigger-${index}`} className="hover:no-underline">
                          <div className="flex items-center gap-3 text-left">
                            <concernStyle.icon className="h-5 w-5 flex-shrink-0" />
                            <span className="font-medium text-eucalypt-800">{condition.condition}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 pb-4">
                          <p className="text-muted-foreground leading-relaxed pl-8">{condition.plain_english}</p>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : (
                !lockedCounts?.special_conditions && <p className="text-muted-foreground">No special conditions found.</p>
              )}
              {!isPaid && lockedCounts?.special_conditions > 0 && (
                <LockedOverlay count={lockedCounts.special_conditions} sectionName="special conditions" />
              )}
            </section>

            {/* Section 4: Things to Clarify */}
            <section className="p-8 border-b border-sandstone-200 bg-amber-50/30" data-testid="section-clarify">
              <h2 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm">4</span>
                Things to Clarify
              </h2>
              {isPaid ? (
                report?.things_to_clarify?.length > 0 ? (
                  <div className="space-y-4">
                    {report.things_to_clarify.map((item, index) => (
                      <div key={index} className="bg-white p-4 border-l-4 border-amber-500">
                        <p className="font-semibold text-eucalypt-800 mb-1 flex items-center gap-2">
                          <HelpCircle className="h-4 w-4 text-amber-600" />{item.item}
                        </p>
                        <p className="text-muted-foreground pl-6">{item.reason}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No unusual items identified.</p>
                )
              ) : (
                <FullyLockedSection count={lockedCounts?.things_to_clarify || 3} sectionName="items to clarify" bgClass="bg-amber-50" />
              )}
            </section>

            {/* Section 5: Questions for Solicitor */}
            <section className="p-8" data-testid="section-questions">
              <h2 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-eucalypt-100 flex items-center justify-center text-eucalypt-700 font-bold text-sm">5</span>
                Questions to Ask Your Solicitor
              </h2>
              {isPaid ? (
                report?.questions_for_solicitor?.length > 0 ? (
                  <div className="bg-eucalypt-50 p-6">
                    <ol className="space-y-3">
                      {report.questions_for_solicitor.map((question, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-eucalypt-200 flex items-center justify-center text-eucalypt-700 font-semibold text-sm flex-shrink-0">{index + 1}</span>
                          <p className="text-eucalypt-700">{question}</p>
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No specific questions identified.</p>
                )
              ) : (
                <FullyLockedSection count={lockedCounts?.questions_for_solicitor || 5} sectionName="questions for your solicitor" bgClass="bg-eucalypt-50" />
              )}
            </section>

            {/* Unlock CTA - Bottom */}
            {hasLockedContent && (
              <div className="px-8 py-8 bg-gradient-to-r from-eucalypt-800 to-eucalypt-900 text-center" data-testid="unlock-cta-bottom">
                <Unlock className="h-8 w-8 text-sandstone-300 mx-auto mb-3" />
                <h3 className="font-heading text-xl text-white mb-2">Unlock Your Complete Report</h3>
                <p className="text-sandstone-300 mb-6 max-w-md mx-auto">
                  Get the full analysis including all special conditions, things to clarify, and tailored questions for your solicitor.
                </p>
                <Button
                  data-testid="unlock-report-bottom-btn"
                  onClick={handleUnlockReport}
                  disabled={isUnlocking}
                  size="lg"
                  className="bg-terracotta-600 hover:bg-terracotta-700 text-white text-lg px-8 py-6"
                >
                  {isUnlocking ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Unlock className="h-5 w-5 mr-2" />}
                  Unlock Full Report — $49.99 AUD
                </Button>
                <p className="text-sandstone-400 text-xs mt-3">Secure payment via Stripe</p>
              </div>
            )}

            {/* Disclaimer */}
            <div className="px-8 py-6 bg-sandstone-100 border-t border-sandstone-200">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong>Disclaimer:</strong> This report is provided for educational purposes only and does not constitute legal advice. 
                Always consult with a qualified solicitor or conveyancer before signing any property contract.
              </p>
            </div>
          </motion.div>

          {/* Actions - only for paid users */}
          {isPaid && (
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <Button data-testid="download-report-btn" onClick={downloadReport} className="bg-terracotta-600 hover:bg-terracotta-700 text-white">
                <Download className="h-4 w-4 mr-2" />Download Report
              </Button>
              <Button data-testid="print-report-btn" onClick={printReport} variant="outline" className="border-terracotta-300 text-terracotta-700 hover:bg-terracotta-50">
                <Printer className="h-4 w-4 mr-2" />Print Report
              </Button>
              <Button data-testid="analyze-another-btn" onClick={() => navigate('/upload')} className="bg-eucalypt-700 hover:bg-eucalypt-800 text-white">
                Analyze Another Contract
              </Button>
              <Button data-testid="back-home-btn" variant="outline" onClick={() => navigate('/')} className="border-eucalypt-300 text-eucalypt-700">
                <Home className="h-4 w-4 mr-2" />Back to Home
              </Button>
            </div>
          )}

          {/* Email Notice - only for paid */}
          {isPaid && (
            <div className="mt-6 text-center p-4 bg-eucalypt-50 border border-eucalypt-200">
              <div className="flex items-center justify-center gap-2 text-eucalypt-700">
                <Mail className="h-4 w-4" />
                <span className="text-sm">A copy of this report has been sent to your email.</span>
              </div>
            </div>
          )}

          <p className="text-center text-sm text-muted-foreground mt-6">
            Questions? Contact <a href="mailto:support@contractwiser.com.au" className="text-eucalypt-700 hover:underline">support@contractwiser.com.au</a>
          </p>
        </div>
      </main>
    </div>
  );
}
